import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import { useMutation } from "@apollo/client";
import { camelCase, get } from "@/utils/nodash";

// Per: https://react-hook-form.com/docs/useform/seterror
// We must start with 'root' so that the error is doesn't prevent
// re-submission after the error has been corrected by the user
export const NON_FIELD_ERROR_KEY = "root.nonFieldError";

export function handleServerSideErrors(errors, incrementScrollToError, setError) {
  if (!errors.length) {
    return;
  }

  errors.forEach(error => {
    setError(error.field === "_All__" ? NON_FIELD_ERROR_KEY : camelCase(error.field), {
      type: "custom",
      message: error.messages[0]
    });
  });

  incrementScrollToError();
}

export default function useDjangoGraphqlForm({
  defaultValues,
  mutation: mutationGql,
  mutationOptions,
  mutationName,
  transformer,
  handleSuccess,
  handleFailure,
  mode,
  onChange: onChangeProp
}) {
  const [scrollToError, setScrollToError] = useState(0);

  const incrementScrollToError = useCallback(() => {
    setScrollToError(scrollToError + 1);
  }, [scrollToError]);

  const [mutation, { loading, error }] = useMutation(mutationGql, mutationOptions);
  const methods = useForm({
    defaultValues,
    mode
  });

  const transformerRef = useRef();
  transformerRef.current = transformer;

  const handleSuccessRef = useRef();
  handleSuccessRef.current = handleSuccess;
  const handleFailureRef = useRef();
  handleFailureRef.current = handleFailure;

  const onChangeRef = useRef();
  onChangeRef.current = onChangeProp;

  const {
    getValues,
    reset,
    setError,
    formState: { errors },
    watch
  } = methods;

  const onSubmit = useCallback(
    async input => {
      try {
        const resp = await mutation({
          variables: {
            input: transformerRef.current ? transformerRef.current(input) : input
          }
        });
        const data = resp.data;
        if (data[mutationName]) {
          if (!data[mutationName].errors.length) {
            if (handleSuccessRef.current) {
              handleSuccessRef.current({
                data,
                api: {
                  reset
                }
              });
            }
          } else if (data[mutationName].errors.length) {
            handleServerSideErrors(data[mutationName].errors, incrementScrollToError, setError);
            if (handleFailureRef.current) {
              handleFailureRef.current(data[mutationName].errors);
            }
          }
        }
      } catch (error) {
        console.log("Error encontered submitting the form", error);
        setError(NON_FIELD_ERROR_KEY, {
          type: "custom",
          message: `Something went wrong. Please try again later. (${error})`
        });
        if (handleFailureRef.current) {
          handleFailureRef.current(error);
        }
      }
    },
    [mutation, setError, reset, mutationName, incrementScrollToError]
  );

  const onChange = useEffect(() => {
    const subscription = watch((values, { type }) => {
      if (type === "change" && onChangeRef.current) {
        onChangeRef.current(values, { onSubmit });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onSubmit]);

  const applyServerErrors = useCallback(
    errors => {
      handleServerSideErrors(errors, incrementScrollToError, setError);
    },
    [incrementScrollToError, setError]
  );

  const id = getValues("id");

  const nonFieldError = get(errors, NON_FIELD_ERROR_KEY)?.message;

  const api = useMemo(
    () => ({
      id,
      methods,
      nonFieldError,
      onSubmit,
      applyServerErrors,
      scrollToError,
      loading,
      error
    }),
    [id, methods, nonFieldError, onSubmit, applyServerErrors, scrollToError, loading, error]
  );

  return api;
}
